import { AxiosHeaderValue, AxiosResponseHeaders } from 'axios';
import dayjs from 'dayjs';

import {
  ApiListResult,
  ApiResult,
  CRAPITagData,
  CRFolderTags,
  CRModelAnomalousTags,
  CRTagAnomaly,
  CRTagData,
  CRTagMinMaxTimeSeries,
  CRTagMinMaxTimeSeriesString,
  CRTagMode,
  TimeSearchResult,
} from '../api';
import { TagResampleMinMaxPayload } from '../tag';

import { HiddenTags } from './analyze-system-browser';

import { TimeValue, TimeValueString } from './index';

import { mapAnomalies } from '@controlrooms/utils';

export type CustomHeader = AxiosResponseHeaders | Partial<AxiosHeaderValue>;
export class TimeSeriesString implements TimeValueString {
  time: number;
  timestamp: string | number;
  value: string | number;

  constructor(init: CRTagMinMaxTimeSeriesString) {
    this.time = dayjs(init.time).valueOf();
    this.timestamp = Number(init.time);
    this.value = init.value;
  }
}

export class TimeSeriesRollingAverage {
  time: number;
  value: number;

  constructor(init: RollingAverageTagData) {
    this.time = dayjs(init.time).valueOf();
    this.value = init.value;
  }
}

export class TimeSeries implements TimeValue {
  max?: number;
  min?: number;
  time: number;
  timestamp: string | number;
  value: number;
  frequent: CRTagMode | number | undefined;
  severity: number | undefined;

  constructor(init: CRTagMinMaxTimeSeries) {
    this.max = init.max_value;
    this.min = init.min_value;
    this.time = dayjs(init.time).valueOf();
    this.timestamp = Number(init.time);
    this.value = init.value;
    this.frequent = init.frequent;
  }
}

export class Mode {
  start: number;
  startTimestamp: string;
  end: number;
  endTimestamp: string;
  value: number;
  type: string;
  tagName?: string;
  limitCondition: string;
  limitId: string | number;

  constructor(init: CRTagMode, value: number, type: string, tagName?: string) {
    this.start = dayjs(init.start_time).valueOf();
    this.startTimestamp = init.start_time;
    this.end = dayjs(init.end_time).valueOf();
    this.endTimestamp = init.end_time;
    this.value = value;
    this.type = type || '';
    this.tagName = tagName || '';
    this.limitCondition = init.limit_condition || 'n/a'; // set 'n/a' if it doesn't come from the payload
    this.limitId = init.limit_id || '';
  }
}

export interface AnomalyRange {
  severity: number;
  start: number;
  end: number;
  yScale: TimeValue[];
}

// export class TagSearchMinMaxResult {
//   folder: number;
//   tagData: CRTagData<TimeSeries, Mode, AnomalyRange>[];

//   constructor(init: CRTagSearchMinMaxResult, interval: number) {
//     this.folder = init.folder;
//     this.tagData = init.tag_data.map((d) => {
//       const timeseries = (d.timeseries ?? []).map((ts) => new TimeSeries(ts));
//       /* eslint-disable @typescript-eslint/no-explicit-any */
//       return {
//         ...d,
//         anomalies: mapAnomalies(d, interval, timeseries),
//         modes: (d.modes ?? [])?.map((m: any) => new Mode(m, m.frequent, 'frequent', d.tag)),
//         highs: (d.highs ?? [])?.map((h: any) => new Mode(h, h.high_limit, 'highs', d.tag)),
//         highHighs: (d.high_highs ?? [])?.map(
//           (hh: any) => new Mode(hh, hh.high_high_limit, 'highHighs', d.tag),
//         ),
//         lows: (d.lows ?? [])?.map((l: any) => new Mode(l, l.low_limit, 'lows', d.tag)),
//         lowLows: (d.low_lows ?? [])?.map(
//           (ll: any) => new Mode(ll, ll.low_low_limit, 'lowLows', d.tag),
//         ),
//         timeseries,
//       };
//       /* eslint-enable @typescript-eslint/no-explicit-any */
//     });
//   }
// }

// export class SearchTagsMinMaxResult implements ApiListResult<TagSearchMinMaxResult> {
//   result: TagSearchMinMaxResult[];
//   request: Record<string, string | number>;
//   headers: CustomHeader;

//   constructor(init: ApiListResult<CRTagSearchMinMaxResult>) {
//     const { interval } = init.result as unknown as TagSearchMinMaxPayload;
//     this.result = (init.result ?? []).map((d) => new TagSearchMinMaxResult(d, interval)) ?? [];
//     this.request = init.request as Record<string, string | number>;
//     this.headers = init.headers;
//   }
// }

interface fetchAllHiddenTagsResponse {
  hidden_tags: HiddenTags[];
}
export class FetchHiddenTagsResult {
  result: HiddenTags[];
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<fetchAllHiddenTagsResponse>) {
    this.result = init.result?.hidden_tags ?? [];
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export class DeleteHiddenTagsResult {
  result: null;
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiListResult<HiddenTags>) {
    this.result = null;
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export class HideTagsResult {
  result: HiddenTags;
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<HiddenTags>) {
    this.result = init.result;
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export interface TagState {
  state_name: string;
  threshold_type: string;
  state_value: string;
  state_id: 1;
}

interface TagStateResponse {
  tag_states: TagState[];
}
export class TagStateResult {
  result: TagState[];
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<TagStateResponse>) {
    this.result = init.result.tag_states;
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export class TransformedTagData {
  result: CRTagData<TimeSeries, Mode, AnomalyRange>[];

  constructor(
    init: CRAPITagData<CRTagMinMaxTimeSeries, CRTagMode, CRTagAnomaly>[],
    interval: number,
  ) {
    const getMinMaxModeValue = (modes: Mode[]) => {
      if (modes.length === 0) return { min: null, max: null };

      if (modes.length === 1) {
        const singleValue = modes[0].value ?? 0;
        return { min: singleValue, max: singleValue };
      }

      const result = modes.reduce(
        (acc, mode) => {
          if (typeof mode.value !== 'number') {
            throw new Error('All Mode objects must have a numeric "value" property.');
          }
          acc.min = Math.min(acc.min, mode.value);
          acc.max = Math.max(acc.max, mode.value);
          return acc;
        },
        { min: modes[0].value, max: modes[0].value },
      );

      return result;
    };

    this.result = (init ?? []).map((d) => {
      const timeseries = (d.timeseries ?? []).map((ts) => new TimeSeries(ts));

      const ranges = (d.timeseries ?? []).reduce(
        (acc, dataPoint) => {
          // Check min and max using min_value and max_value first
          if (dataPoint.min_value !== null && dataPoint.min_value !== undefined) {
            acc.min =
              acc.min === null ? dataPoint.min_value : Math.min(acc.min, dataPoint.min_value);
          }
          if (dataPoint.max_value !== null && dataPoint.max_value !== undefined) {
            acc.max =
              acc.max === null ? dataPoint.max_value : Math.max(acc.max, dataPoint.max_value);
          }

          // If min and max are still null, fall back to dataPoint.value
          if (dataPoint.value !== null && dataPoint.value !== undefined) {
            if (acc.min === null) {
              acc.min = dataPoint.value;
            } else {
              acc.min = Math.min(acc.min, dataPoint.value);
            }

            if (acc.max === null) {
              acc.max = dataPoint.value;
            } else {
              acc.max = Math.max(acc.max, dataPoint.value);
            }
          }

          return acc;
        },
        { min: null as number | null, max: null as number | null },
      );

      return {
        ...d,
        anomalies: mapAnomalies(d, interval, timeseries),
        modes: (d.modes ?? []).flatMap((m) =>
          'frequent' in m ? [new Mode(m, m.frequent, 'frequent', d.tag)] : [],
        ),
        highs: (d.highs ?? []).flatMap((h) =>
          'high_limit' in h ? [new Mode(h, h.high_limit, 'highs', d.tag)] : [],
        ),
        highHighs: (d.high_highs ?? []).flatMap((hh) =>
          'high_high_limit' in hh ? [new Mode(hh, hh.high_high_limit, 'highHighs', d.tag)] : [],
        ),
        lows: (d.lows ?? []).flatMap((l) =>
          'low_limit' in l ? [new Mode(l, l.low_limit, 'lows', d.tag)] : [],
        ),
        lowLows: (d.low_lows ?? []).flatMap((ll) =>
          'low_low_limit' in ll ? [new Mode(ll, ll.low_low_limit, 'lowLows', d.tag)] : [],
        ),
        timeseries,
        ranges: {
          timeseries: ranges,
          frequent: getMinMaxModeValue(
            (d.modes ?? [])?.flatMap((m) =>
              'frequent' in m ? [new Mode(m, m.frequent, 'frequent', d.tag)] : [],
            ),
          ),
          highs: getMinMaxModeValue(
            (d.highs ?? [])?.flatMap((h) =>
              'high_limit' in h ? [new Mode(h, h.high_limit, 'highs', d.tag)] : [],
            ),
          ),
          highHighs: getMinMaxModeValue(
            (d.high_highs ?? [])?.flatMap((hh) =>
              'high_high_limit' in hh ? [new Mode(hh, hh.high_high_limit, 'highHighs', d.tag)] : [],
            ),
          ),
          lows: getMinMaxModeValue(
            (d.lows ?? [])?.flatMap((l) =>
              'low_limit' in l ? [new Mode(l, l.low_limit, 'lows', d.tag)] : [],
            ),
          ),
          lowLows: getMinMaxModeValue(
            (d.low_lows ?? [])?.flatMap((ll) =>
              'low_low_limit' in ll ? [new Mode(ll, ll.low_low_limit, 'lowLows', d.tag)] : [],
            ),
          ),
        },
      };
    });
  }
}

export class LimitData {
  lows: Mode[];
  lowLows: Mode[];
  highs: Mode[];
  highHighs: Mode[];

  constructor(init: ApiResult<any>) {
    const data = init.result[0];
    this.lows = data.lows ? this.restructureModes(data.lows, data.tag) : [];
    this.lowLows = data.low_lows ? this.restructureModes(data.low_lows, data.tag) : [];
    this.highs = data.highs ? this.restructureModes(data.highs, data.tag) : [];
    this.highHighs = data.high_highs ? this.restructureModes(data.high_highs, data.tag) : [];
  }

  restructureModes(modes: any[], tag: string): Mode[] {
    return modes.map((limit) => ({
      start: new Date(limit.start_time).getTime(),
      end: new Date(limit.end_time).getTime(),
      startTimestamp: limit.start_time,
      endTimestamp: limit.end_time,
      value: limit.value,
      limitCondition: limit.limit_condition,
      limitId: limit.limit_id,
      type: limit.type || '',
      tagName: tag || '',
    }));
  }
}

export class ResampleTagsMinMaxResult
  implements ApiListResult<CRTagData<TimeSeries, Mode, AnomalyRange>>
{
  result: CRTagData<TimeSeries, Mode, AnomalyRange>[];
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiListResult<CRAPITagData<CRTagMinMaxTimeSeries, CRTagMode, CRTagAnomaly>>) {
    const { interval } = init.request as unknown as TagResampleMinMaxPayload;
    const transformedData = new TransformedTagData(init.result, Number(interval));

    this.result = transformedData.result;
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export class ResampleTagsMinMaxResultV2
  implements ApiListResult<CRTagData<TimeSeries, Mode, AnomalyRange>>
{
  result: CRTagData<TimeSeries, Mode, AnomalyRange>[];
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiListResult<CRAPITagData<CRTagMinMaxTimeSeries, CRTagMode, CRTagAnomaly>>) {
    const { interval } = init.request as unknown as TagResampleMinMaxPayload;
    const transformedData = new TransformedTagData(init.result, Number(interval));

    this.result = transformedData.result;
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

type RollingAverageTagData = {
  time: string;
  value: number;
  min_value?: number;
  max_value?: number;
};

export interface RollingAverageTagResponse {
  tag_name: string;
  rolling_average_data: RollingAverageTagData[];
  processing_time_ms: number;
  data_source: string;
  mean_value: number;
  mean_average_deviation: number;
  derived_tag_name?: string;
}

export interface TransformedRollingAverageTag {
  tagName: string;
  rollingAverageData: TimeSeriesRollingAverage[];
  processingTime: number;
  dataSource: string;
  mean: number;
  mad: number;
  derivedTagName?: string;
}

export class RollingAverageTagsMinMaxResult {
  result: TransformedRollingAverageTag;
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<RollingAverageTagResponse>) {
    this.result = {
      tagName: init.result.tag_name,
      rollingAverageData: init.result.rolling_average_data.map(
        (d) => new TimeSeriesRollingAverage(d),
      ),
      processingTime: init.result.processing_time_ms,
      dataSource: init.result.data_source,
      mean: init.result.mean_value,
      mad: init.result.mean_average_deviation,
      derivedTagName: init.result.derived_tag_name,
    };
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

type RateOfChangeData = {
  time: string;
  value: number;
  min_value: number;
  max_value: number;
};

export interface RateOfChangeResponse {
  tag_name: string;
  rate_of_change_data: RateOfChangeData[];
  processing_time_ms: number;
  data_source: string;
  mean_value: number;
  mean_average_deviation: number;
  derived_tag_name?: string;
  time_resolution: string;
}

export interface TransformedRateOfChangeTag {
  tagName: string;
  rateOfChangeData: TimeSeriesRollingAverage[];
  processingTime: number;
  dataSource: string;
  mean: number;
  mad: number;
  derivedTagName?: string;
  timeResolution: string;
}

export class RateOfChangeTagsMinMaxResult {
  result: TransformedRateOfChangeTag;
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<RateOfChangeResponse>) {
    this.result = {
      tagName: init.result.tag_name,
      rateOfChangeData: init.result.rate_of_change_data.map(
        (d) =>
          new TimeSeriesRollingAverage({
            time: d.time,
            value: d.value,
            min_value: d.min_value,
            max_value: d.max_value,
          }),
      ),
      processingTime: init.result.processing_time_ms,
      dataSource: init.result.data_source,
      mean: init.result.mean_value,
      mad: init.result.mean_average_deviation,
      derivedTagName: init.result.derived_tag_name,
      timeResolution: init.result.time_resolution,
    };
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

type RollingAverageWithAnomaliesData = {
  time: string;
  value: number;
  min_value: number;
  max_value: number;
};

type AnomalyData = {
  time: string;
  value: number;
};

export interface RollingAverageWithAnomaliesResponse {
  tag_name: string;
  rolling_average_data: RollingAverageWithAnomaliesData[];
  anomalies: AnomalyData[];
}

export class RollingAverageWithAnomaliesResult {
  result: CRTagData<TimeSeries, Mode, AnomalyRange>[];
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<RollingAverageWithAnomaliesResponse>) {
    const rollingAverageData = init.result.rolling_average_data || [];
    const anomalies = init.result.anomalies || [];

    // Transform rolling average data to TimeSeries format
    const timeseries: TimeSeries[] = rollingAverageData.map(
      (d) =>
        ({
          time: dayjs(d.time).valueOf(),
          timestamp: dayjs(d.time).valueOf(),
          value: d.value,
          min: d.min_value,
          max: d.max_value,
          frequent: undefined,
          severity: undefined,
        } as TimeSeries),
    );

    // Calculate ranges from the rolling average data
    const ranges = rollingAverageData.reduce(
      (acc, dataPoint) => {
        if (dataPoint.min_value !== null && dataPoint.min_value !== undefined) {
          acc.min = acc.min === null ? dataPoint.min_value : Math.min(acc.min, dataPoint.min_value);
        }
        if (dataPoint.max_value !== null && dataPoint.max_value !== undefined) {
          acc.max = acc.max === null ? dataPoint.max_value : Math.max(acc.max, dataPoint.max_value);
        }
        return acc;
      },
      { min: null as number | null, max: null as number | null },
    );

    // Transform anomalies to AnomalyRange format using mapAnomalies utility
    // Note: AnomalyData.value represents the rolling average data value at anomaly time.
    // mapAnomalies will use this value as severity and filter out anomalies where value === 0.
    const interval =
      ((init.request as Record<string, unknown>)?.interval as number | undefined) || 60;

    const tagName = init.result.tag_name;
    const emptyModeArray: Mode[] = [];
    const nullRange = { min: null, max: null };

    const anomalyRanges = mapAnomalies(
      {
        tag: tagName,
        descr: tagName,
        tag_display_name: tagName,
        timeseries: [],
        anomalies,
        modes: [],
        highs: [],
        high_highs: [],
        lows: [],
        low_lows: [],
      },
      interval,
      timeseries,
    );

    this.result = [
      {
        tag: tagName,
        descr: tagName,
        timeseries,
        anomalies: anomalyRanges,
        modes: emptyModeArray,
        highs: emptyModeArray,
        highHighs: emptyModeArray,
        lows: emptyModeArray,
        lowLows: emptyModeArray,
        ranges: {
          timeseries: ranges,
          frequent: nullRange,
          highs: nullRange,
          highHighs: nullRange,
          lows: nullRange,
          lowLows: nullRange,
        },
      },
    ];
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export interface RateOfChangeWithAnomaliesResponse {
  tag_name: string;
  rate_of_change_data: RateOfChangeData[];
  anomalies: AnomalyData[];
  derived_tag_name?: string;
  time_resolution: string;
  mean_value: number;
  mean_average_deviation: number;
}

// Extended CRTagData type with derivedTagName and timeResolution for Rate of Change
export type CRTagDataWithDerivedInfo<T, M, A> = CRTagData<T, M, A> & {
  derivedTagName?: string;
  timeResolution?: string;
  mean?: number;
  mad?: number;
  upperLimit?: number;
  lowerLimit?: number;
  anomalyCount?: number;
};

export class RateOfChangeWithAnomaliesResult {
  result: CRTagDataWithDerivedInfo<TimeSeries, Mode, AnomalyRange>[];
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<RateOfChangeWithAnomaliesResponse>) {
    const rateOfChangeData = init.result.rate_of_change_data || [];
    const anomalies = init.result.anomalies || [];
    const derivedTagName = init.result.derived_tag_name || '';
    const timeResolution = init.result.time_resolution || 'days';
    // Transform rate of change data to TimeSeries format
    const timeseries: TimeSeries[] = rateOfChangeData.map(
      (d) =>
        ({
          time: dayjs(d.time).valueOf(),
          timestamp: dayjs(d.time).valueOf(),
          value: d.value,
          min: d.min_value,
          max: d.max_value,
          frequent: undefined,
          severity: undefined,
        } as TimeSeries),
    );

    // Calculate ranges from the rate of change data
    const ranges = rateOfChangeData.reduce(
      (acc, dataPoint) => {
        if (dataPoint.min_value !== null && dataPoint.min_value !== undefined) {
          acc.min = acc.min === null ? dataPoint.min_value : Math.min(acc.min, dataPoint.min_value);
        }
        if (dataPoint.max_value !== null && dataPoint.max_value !== undefined) {
          acc.max = acc.max === null ? dataPoint.max_value : Math.max(acc.max, dataPoint.max_value);
        }
        return acc;
      },
      { min: null as number | null, max: null as number | null },
    );

    // Transform anomalies to AnomalyRange format using mapAnomalies utility
    const interval =
      ((init.request as Record<string, unknown>)?.interval as number | undefined) || 60;

    const tagName = init.result.tag_name;
    const emptyModeArray: Mode[] = [];
    const nullRange = { min: null, max: null };

    const anomalyRanges = mapAnomalies(
      {
        tag: tagName,
        descr: tagName,
        tag_display_name: tagName,
        timeseries: [],
        anomalies,
        modes: [],
        highs: [],
        high_highs: [],
        lows: [],
        low_lows: [],
      },
      interval,
      timeseries,
    );

    this.result = [
      {
        tag: tagName,
        descr: tagName,
        timeseries,
        anomalies: anomalyRanges,
        modes: emptyModeArray,
        highs: emptyModeArray,
        highHighs: emptyModeArray,
        lows: emptyModeArray,
        lowLows: emptyModeArray,
        ranges: {
          timeseries: ranges,
          frequent: nullRange,
          highs: nullRange,
          highHighs: nullRange,
          lows: nullRange,
          lowLows: nullRange,
        },
        derivedTagName,
        timeResolution,
        mean: init.result.mean_value,
        mad: init.result.mean_average_deviation,
      },
    ];
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

type TimeToValueForecastData = {
  time: string;
  value: number;
  min_value: number;
  max_value: number;
};

type TimeToValueAnchor = {
  time: string;
  value: number;
  min_value: number;
  max_value: number;
};

export interface TimeToValueForecastResponse {
  tag_name: string;
  time_to_value_forecast_data: TimeToValueForecastData[];
  time_to_value_forecast_anchors: TimeToValueAnchor[];
  processing_time_ms: number;
  data_source: string;
  mean_value: number | null;
  mean_average_deviation: number | null;
  anomaly_count: number;
  derived_tag_name?: string;
  time_resolution?: string;
}

export interface TransformedTimeToValueForecastTag {
  tagName: string;
  timeToValueForecastData: TimeSeriesRollingAverage[];
  timeToValueAnchors: TimeSeriesRollingAverage[];
  processingTime: number;
  dataSource: string;
  mean: number | null;
  mad: number | null;
  derivedTagName?: string;
}

export class TimeToValueForecastTagsMinMaxResult {
  result: TransformedTimeToValueForecastTag;
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<TimeToValueForecastResponse>) {
    this.result = {
      tagName: init.result.tag_name,
      timeToValueForecastData: init.result.time_to_value_forecast_data.map(
        (d) =>
          new TimeSeriesRollingAverage({
            time: d.time,
            value: d.value,
            min_value: d.min_value,
            max_value: d.max_value,
          }),
      ),
      timeToValueAnchors:
        init.result?.time_to_value_forecast_anchors?.map(
          (d) =>
            new TimeSeriesRollingAverage({
              time: d.time,
              value: d.value,
              min_value: d.min_value,
              max_value: d.max_value,
            }),
        ) || [],
      processingTime: init.result.processing_time_ms,
      dataSource: init.result.data_source,
      mean: init.result.mean_value,
      mad: init.result.mean_average_deviation,
      derivedTagName: init.result.derived_tag_name,
    };
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export interface TimeToValueForecastWithAnomaliesResponse {
  tag_name: string;
  time_to_value_forecast_data: TimeToValueForecastData[];
  anomalies: AnomalyData[];
  processing_time_ms: number;
  data_source: string;
  mean_value: number | null;
  mean_average_deviation: number | null;
  upper_limit: number;
  lower_limit: number;
  anomaly_count: number;
  derived_tag_name?: string;
  time_resolution?: string;
}

type ValueAtDateForecastData = {
  time: string;
  value: number;
  min_value: number;
  max_value: number;
};

type ValueAtDateAnchor = {
  time: string;
  value: number;
  min_value: number;
  max_value: number;
};

export interface ValueAtDateForecastResponse {
  tag_name: string;
  value_at_date_forecast_data: ValueAtDateForecastData[];
  value_at_date_forecast_anchors: ValueAtDateAnchor[];
  processing_time_ms: number;
  data_source: string;
  mean_value: number | null;
  mean_average_deviation: number | null;
  anomaly_count: number;
  derived_tag_name?: string;
  target_date?: string;
}

export interface TransformedValueAtDateForecastTag {
  tagName: string;
  valueAtDateForecastData: TimeSeriesRollingAverage[];
  valueAtDateAnchors: TimeSeriesRollingAverage[];
  processingTime: number;
  dataSource: string;
  mean: number | null;
  mad: number | null;
  derivedTagName?: string;
}

export class ValueAtDateForecastTagsMinMaxResult {
  result: TransformedValueAtDateForecastTag;
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<ValueAtDateForecastResponse>) {
    this.result = {
      tagName: init.result.tag_name,
      valueAtDateForecastData: init.result.value_at_date_forecast_data.map(
        (d) =>
          new TimeSeriesRollingAverage({
            time: d.time,
            value: d.value,
            min_value: d.min_value,
            max_value: d.max_value,
          }),
      ),
      valueAtDateAnchors:
        init.result?.value_at_date_forecast_anchors?.map(
          (d) =>
            new TimeSeriesRollingAverage({
              time: d.time,
              value: d.value,
              min_value: d.min_value,
              max_value: d.max_value,
            }),
        ) || [],
      processingTime: init.result.processing_time_ms,
      dataSource: init.result.data_source,
      mean: init.result.mean_value,
      mad: init.result.mean_average_deviation,
      derivedTagName: init.result.derived_tag_name,
    };
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export interface ValueAtDateForecastWithAnomaliesResponse {
  tag_name: string;
  value_at_date_forecast_data: ValueAtDateForecastData[];
  anomalies: AnomalyData[];
  processing_time_ms: number;
  data_source: string;
  mean_value: number | null;
  mean_average_deviation: number | null;
  upper_limit: number;
  lower_limit: number;
  anomaly_count: number;
  derived_tag_name?: string;
  target_date?: string;
}

export class ValueAtDateForecastWithAnomaliesResult {
  result: CRTagDataWithDerivedInfo<TimeSeries, Mode, AnomalyRange>[];
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<ValueAtDateForecastWithAnomaliesResponse>) {
    const anomalies = init.result.anomalies || [];
    const derivedTagName = init.result.derived_tag_name || '';
    // Transform value at date forecast data to TimeSeries format
    const timeseries = init.result.value_at_date_forecast_data.map(
      (d) =>
        ({
          time: dayjs(d.time).valueOf(),
          timestamp: dayjs(d.time).valueOf(),
          value: d.value,
          min: d.min_value,
          max: d.max_value,
          frequent: undefined,
          severity: undefined,
        } as TimeSeries),
    );

    // Calculate range from timeseries data
    const ranges = timeseries.reduce(
      (acc, ts) => {
        if (ts.value !== null && ts.value !== undefined) {
          if (acc.min === null || ts.value < acc.min) {
            acc.min = ts.value;
          }
          if (acc.max === null || ts.value > acc.max) {
            acc.max = ts.value;
          }
        }
        return acc;
      },
      { min: null as number | null, max: null as number | null },
    );

    // Transform anomalies to AnomalyRange format using mapAnomalies utility
    const interval =
      ((init.request as Record<string, unknown>)?.interval as number | undefined) || 60;

    const tagName = init.result.tag_name;
    const emptyModeArray: Mode[] = [];
    const nullRange = { min: null, max: null };

    const anomalyRanges = mapAnomalies(
      {
        tag: tagName,
        descr: tagName,
        tag_display_name: tagName,
        timeseries: [],
        anomalies,
        modes: [],
        highs: [],
        high_highs: [],
        lows: [],
        low_lows: [],
      },
      interval,
      timeseries,
    );

    this.result = [
      {
        tag: tagName,
        descr: tagName,
        timeseries,
        anomalies: anomalyRanges,
        modes: emptyModeArray,
        highs: emptyModeArray,
        highHighs: emptyModeArray,
        lows: emptyModeArray,
        lowLows: emptyModeArray,
        ranges: {
          timeseries: ranges,
          frequent: nullRange,
          highs: nullRange,
          highHighs: nullRange,
          lows: nullRange,
          lowLows: nullRange,
        },
        mean: init.result.mean_value ?? undefined,
        mad: init.result.mean_average_deviation ?? undefined,
        upperLimit: init.result.upper_limit,
        lowerLimit: init.result.lower_limit,
        anomalyCount: init.result.anomaly_count,
        derivedTagName: derivedTagName,
      },
    ];
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export class TimeToValueForecastWithAnomaliesResult {
  result: CRTagDataWithDerivedInfo<TimeSeries, Mode, AnomalyRange>[];
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiResult<TimeToValueForecastWithAnomaliesResponse>) {
    const anomalies = init.result.anomalies || [];
    const derivedTagName = init.result.derived_tag_name || '';
    const timeResolution = init.result.time_resolution || 'days';
    // Transform time to value forecast data to TimeSeries format
    const timeseries = init.result.time_to_value_forecast_data.map(
      (d) =>
        ({
          time: dayjs(d.time).valueOf(),
          timestamp: dayjs(d.time).valueOf(),
          value: d.value,
          min: d.min_value,
          max: d.max_value,
          frequent: undefined,
          severity: undefined,
        } as TimeSeries),
    );

    // Calculate range from timeseries data
    const ranges = timeseries.reduce(
      (acc, ts) => {
        if (ts.value !== null && ts.value !== undefined) {
          if (acc.min === null || ts.value < acc.min) {
            acc.min = ts.value;
          }
          if (acc.max === null || ts.value > acc.max) {
            acc.max = ts.value;
          }
        }
        return acc;
      },
      { min: null as number | null, max: null as number | null },
    );

    // Transform anomalies to AnomalyRange format using mapAnomalies utility
    const interval =
      ((init.request as Record<string, unknown>)?.interval as number | undefined) || 60;

    const tagName = init.result.tag_name;
    const emptyModeArray: Mode[] = [];
    const nullRange = { min: null, max: null };

    const anomalyRanges = mapAnomalies(
      {
        tag: tagName,
        descr: tagName,
        tag_display_name: tagName,
        timeseries: [],
        anomalies,
        modes: [],
        highs: [],
        high_highs: [],
        lows: [],
        low_lows: [],
      },
      interval,
      timeseries,
    );

    this.result = [
      {
        tag: tagName,
        descr: tagName,
        timeseries,
        anomalies: anomalyRanges,
        modes: emptyModeArray,
        highs: emptyModeArray,
        highHighs: emptyModeArray,
        lows: emptyModeArray,
        lowLows: emptyModeArray,
        ranges: {
          timeseries: ranges,
          frequent: nullRange,
          highs: nullRange,
          highHighs: nullRange,
          lows: nullRange,
          lowLows: nullRange,
        },
        derivedTagName,
        timeResolution,
        mean: init.result.mean_value ?? undefined,
        mad: init.result.mean_average_deviation ?? undefined,
      },
    ];
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export interface PredictedTagResponse {
  tag_name: string;
  timeseries: CRTagMinMaxTimeSeries[];
}

export interface TransformedPredictedTag {
  tag_name: string;
  timeseries: TimeSeries[];
}

export class PredictedTagsMinMaxResult {
  result: TransformedPredictedTag[];
  request: Record<string, string | number>;
  headers: CustomHeader;

  constructor(init: ApiListResult<PredictedTagResponse>) {
    this.result = (init.result ?? []).map((item: PredictedTagResponse) => ({
      tag_name: item?.tag_name,
      timeseries: (item.timeseries ?? []).map((ts: CRTagMinMaxTimeSeries) => new TimeSeries(ts)),
    }));
    this.request = init.request as Record<string, string | number>;
    this.headers = init.headers;
  }
}

export interface TooltipData extends TimeSeries {
  frequent: number;
  uom: string;
}
export interface TooltipContentData {
  tooltipData: TooltipData[];
  limitArrayData: Mode[];
}

export class AnomalousTagsResult implements ApiListResult<CRFolderTags> {
  result: CRFolderTags[];
  request: Record<string, string | number[]>;
  headers: CustomHeader;

  constructor(init: ApiListResult<CRFolderTags>) {
    const _result = init.result ?? [];

    this.request = init.request as Record<string, string | number[]>;
    this.headers = init.headers;

    if (!init.result) {
      this.result = (this.request.folders as unknown as number[]).map((f) => ({
        folder: f,
        tags: [],
      }));
    } else {
      (this.request.folders as unknown as number[]).forEach((f) => {
        if (!_result.find((t) => t.folder === f)) {
          _result.push({ folder: f, tags: [] });
        }
      });
      this.result = _result;
    }
  }
}

export class AnomalousModelTagsResult implements ApiListResult<CRModelAnomalousTags> {
  result: CRModelAnomalousTags[];
  request: Record<string, string | number[]>;
  headers: CustomHeader;

  constructor(init: ApiListResult<CRModelAnomalousTags>) {
    this.result = init.result ?? [];
    this.request = init.request as Record<string, string | number[]>;
    this.headers = init.headers;
  }
}
export class EventSearchResult implements ApiListResult<TimeSearchResult> {
  result: TimeSearchResult[];
  request: Record<string, string | number[]>;
  headers: CustomHeader;

  constructor(init: ApiListResult<TimeSearchResult>) {
    this.result = init.result ?? [];

    this.request = init.request as Record<string, string | number[]>;
    this.headers = init.headers;
  }
}
